// UI-Sprache: Provider + Hooks.
//
// Verwendung in einer Komponente:
//
//     const t = useT();
//     …
//     <button>{t.common.abbrechen}</button>
//     <span>{t.tasks.offeneAufgaben(n)}</span>
//
// Ausserhalb einer Komponente (Toast-Helfer, Formatierer, ui/labels.ts):
//
//     import { katalog } from "../i18n/lang";
//     const t = katalog();   // liest denselben Speicher, kein zweiter Zustand
//
// Regelwerk + Scope: docs/i18n.md.
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  useSyncExternalStore,
  ReactNode,
} from "react";
import { api } from "../api";
import {
  getLang,
  istLang,
  katalog,
  setLangLokal,
  spracheAnsHtml,
  subscribeLang,
  type Katalog,
  type Lang,
} from "./lang";

export { getLang, katalog, istLang, DEFAULT_LANG } from "./lang";
export type { Katalog, Lang } from "./lang";

interface LanguageCtx {
  lang: Lang;
  /** Schaltet sofort um und speichert am Nutzer. Wirft, wenn der PUT scheitert
   *  — die Oberfläche steht dann bereits auf der neuen Sprache, aber der
   *  Aufrufer soll sagen können, dass es nicht gespeichert wurde. */
  setLang: (lang: Lang) => Promise<void>;
  /** true, solange der PUT läuft (für ein „· speichere …" am Schalter). */
  speichert: boolean;
}

const Ctx = createContext<LanguageCtx | null>(null);

export function LanguageProvider({ children }: { children: ReactNode }) {
  const lang = useSyncExternalStore(subscribeLang, getLang);
  const [speichert, setSpeichert] = useState(false);

  // index.html trägt lang="de". Kommt aus dem localStorage "en", muss das
  // Dokument mitziehen — sonst behauptet die Seite eine Sprache, die sie nicht
  // spricht.
  useEffect(() => {
    spracheAnsHtml(lang);
  }, [lang]);

  // Server-Rehydrierung: die Sprache ist eine NUTZER- und keine
  // Browser-Einstellung (dieselbe Begründung wie bei `mail_inbox_depth`) und
  // gilt deshalb auf jedem Gerät. Fehler werden geschluckt: wer nicht
  // angemeldet ist (Login-Maske) oder gerade keine Settings lesen kann, bekommt
  // den localStorage-Stand — eine Präferenz darf den Start nicht blockieren.
  useEffect(() => {
    let lebt = true;
    void api
      .get<{ language?: unknown }>("/settings")
      .then((res) => {
        if (lebt && istLang(res?.language)) setLangLokal(res.language);
      })
      .catch(() => {
        /* siehe oben */
      });
    return () => {
      lebt = false;
    };
  }, []);

  const setLang = useCallback(async (naechste: Lang) => {
    // Erst umschalten, dann speichern: die Umstellung ist sofort sichtbar und
    // hängt nicht am Netz.
    setLangLokal(naechste);
    setSpeichert(true);
    try {
      // Partieller PUT — die anderen Einstellungen bleiben unberührt.
      await api.put("/settings", { language: naechste });
    } finally {
      setSpeichert(false);
    }
  }, []);

  const wert = useMemo(() => ({ lang, setLang, speichert }), [lang, setLang, speichert]);
  return <Ctx.Provider value={wert}>{children}</Ctx.Provider>;
}

/** Aktive Sprache + Umschalter (braucht den Provider). */
export function useLang(): LanguageCtx {
  const v = useContext(Ctx);
  if (!v) throw new Error("useLang outside LanguageProvider");
  return v;
}

/** Der getypte Katalog der aktiven Sprache.
 *
 *  Bewusst OHNE Context: `useT` liest den Speicher aus lang.ts direkt und
 *  funktioniert damit auch in Bäumen, die (etwa in einem Test) ohne Provider
 *  gerendert werden. Ein fehlender Provider soll eine Beschriftung nicht in
 *  einen Absturz verwandeln. */
export function useT(): Katalog {
  return katalog(useSyncExternalStore(subscribeLang, getLang));
}
